import { Injectable } from '@angular/core'
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router'
import { AuthManager } from './AuthManager'
import { ApplicationArea } from '../services/ppv.api'
import { Observable } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'

/**
 * Restricts Hospital Manager (HM) users to the hospital dashboard only.
 *
 * HM users may only reach `leader-toolkit-hospital-dashboard`; any other Leader Toolkit
 * route they try to open (navigation, deep link or an emailed link) is redirected to the
 * dashboard. Query parameters are preserved so the dashboard can open the relevant dialog
 * (e.g. `?taskId=`, `?goalTargetId=`, `?meetingId=`).
 */
@Injectable()
export class HmRestrictionGuard {
   private static readonly HospitalDashboardRoute = 'leader-toolkit-hospital-dashboard'

   constructor(private authMgr: AuthManager, private router: Router) {}

   canActivate(
      route: ActivatedRouteSnapshot,
      state: RouterStateSnapshot
   ): boolean | UrlTree | Observable<boolean | UrlTree> {
      return this.authMgr.ssoLogin$.pipe(
         switchMap(() =>
            this.authMgr.getCurrentUser().pipe(
               map((user: any) => {
                  const isHm = (user?.roles || []).some(
                     (r: any) => r?.application === ApplicationArea.LTK && r?.roleName === 'HM'
                  )
                  if (!isHm) {
                     return true
                  }

                  const targetPath = route.routeConfig?.path || ''
                  if (targetPath.startsWith(HmRestrictionGuard.HospitalDashboardRoute)) {
                     return true
                  }

                  // Keep the query string (taskId / goalTargetId / meetingId) so the dashboard
                  // can open the matching dialog.
                  const queryParams = this.router.parseUrl(state.url).queryParams
                  return this.router.createUrlTree(['/' + HmRestrictionGuard.HospitalDashboardRoute], { queryParams })
               })
            )
         )
      )
   }
}
